import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

const BOOKMAKER_DISPLAY_NAMES = {
    'sportsbet': 'Sportsbet',
    'tab': 'TAB',
    'pointsbet': 'PointsBet',
    'neds': 'Neds',
    'betright': 'BetRight',
    'bet365': 'Bet365',
    'betmakers': 'Betmakers',
    'ladbrokes': 'Ladbrokes',
    'fanduel': 'FanDuel',
    'draftkings': 'DraftKings',
    'betmgm': 'BetMGM',
    'dabble': 'Dabble',
    'pinnacle': 'Pinnacle',
};

const formatBetDescription = (bet, strategy, side) => {
    const player = bet.player || 'Unknown';
    const market = bet.market || bet.marketTypeDisplay || '';
    const stat = (bet.marketTypeDisplay || bet.marketType || '').toLowerCase();

    const milestoneMatch = market.match(/To Record (\d+\+)\s+(\w+)/i);
    if (milestoneMatch) {
        return `${player} ${milestoneMatch[1]} ${milestoneMatch[2].toLowerCase()}`;
    }

    if (strategy === 'player_line' && bet.selection && bet.line != null) {
        const dir = bet.selection.toUpperCase() === 'OVER' ? 'O' : 'U';
        return `${player} ${dir}${bet.line} ${stat}`.trim();
    }

    if (strategy === 'arbs' && bet.line != null) {
        if (side === 'over' && bet.overSource === 'prop' && bet.overSelection) {
            const propMatch = bet.overSelection.match(/(OVER|UNDER)\s+([\d.]+)\s+(\w+)/i);
            if (propMatch) {
                const threshold = Math.ceil(parseFloat(propMatch[2]));
                return `${player} ${threshold}+ ${propMatch[3].toLowerCase()}`;
            }
            return `${player} ${bet.overSelection}`;
        }
        const dir = side === 'under' ? 'U' : 'O';
        return `${player} ${dir}${bet.line} ${stat}`.trim();
    }

    return player !== 'Unknown' && market ? `${player} - ${market}` : market || 'Unknown bet';
};

function AddToTrackerModal({ bet, onClose, bookmakerLogos, computedEv, computedFairOdds, computedBestBook, strategy, arbStake, unitSize = 20, onAddToTracker, isAddingBet, sport }) {
    const [stake, setStake] = useState('');
    const stakeInputRef = useRef(null);

    const initArbStakes = useMemo(() => {
        if (strategy !== 'arbs' || !arbStake || arbStake <= 0 || !bet?.overOdds || !bet?.underOdds) return null;
        const total = arbStake;
        const invSum = 1 / bet.overOdds + 1 / bet.underOdds;
        const overStake = total * (1 / bet.overOdds) / invSum;
        const underStake = total - overStake;
        return { over: overStake.toFixed(2), under: underStake.toFixed(2) };
    }, [strategy, arbStake, bet?.overOdds, bet?.underOdds]);

    const [arbOverStake, setArbOverStake] = useState('');
    const [arbUnderStake, setArbUnderStake] = useState('');

    useEffect(() => {
        if (initArbStakes) {
            setArbOverStake(initArbStakes.over);
            setArbUnderStake(initArbStakes.under);
        }
    }, [initArbStakes]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const timer = setTimeout(() => stakeInputRef.current?.focus(), 50);
        return () => {
            document.body.style.overflow = '';
            clearTimeout(timer);
        };
    }, []);

    if (!bet) return null;

    const isMarketValue = strategy === 'market_value' || strategy === 'sb_baseline' || strategy === 'market_and_implied_ev';
    const isArbs = strategy === 'arbs';
    const opp = isMarketValue ? bet.valueOpportunities?.[0] : null;

    const shortenMatch = (match) => {
        if (!match) return '';
        if (sport === 'afl') return match;
        const parts = match.split(' vs ');
        if (parts.length !== 2) return match;
        const last = (t) => { const w = t.trim().split(' '); return w[w.length - 1]; };
        return `${last(parts[0])} vs ${last(parts[1])}`;
    };

    const headerRight = isArbs
        ? `${bet.line} ${bet.marketTypeDisplay || bet.marketType || ''}`
        : isMarketValue
        ? bet.market || ''
        : (() => {
            const prefix = bet.selection === 'OVER' ? 'o' : bet.selection === 'UNDER' ? 'u' : '';
            return `${prefix}${bet.line} ${bet.marketTypeDisplay || bet.marketType || ''}`;
        })();

    const bestBookmaker = isArbs
        ? null
        : isMarketValue
        ? opp?.bookmaker
        : (computedBestBook?.bookmaker || bet.bestBookmaker);

    const bestOfferedPrice = isArbs
        ? null
        : isMarketValue
        ? opp?.odds
        : (computedBestBook?.odds || bet.bestOdds);

    const edgeValue = isArbs
        ? bet.arbPercentage
        : isMarketValue
        ? opp?.impliedEdgePercentage
        : (computedEv != null ? computedEv : bet.expectedValue);

    const edgeLabel = isArbs ? 'Arb' : isMarketValue ? 'Edge' : 'EV';

    const bookmakerOddsEntries = isArbs
        ? []
        : isMarketValue
        ? (() => {
            const entries = bet.bookmakerOdds || [];
            if (strategy !== 'sb_baseline') return entries;
            const keep = new Set(['sportsbet']);
            if (bestBookmaker) keep.add(bestBookmaker);
            return entries.filter(e => keep.has(e.bookmaker));
        })()
        : (bet.bookmakerData || []);

    const stakeNum = parseFloat(stake);
    const hasPayout = stake && !isNaN(stakeNum) && stakeNum > 0 && bestOfferedPrice;
    const payout = hasPayout ? (stakeNum * bestOfferedPrice).toFixed(2) : null;

    const renderBookmakerLogo = (bookmaker, size = 'w-7 h-7') => {
        const logo = bookmakerLogos?.[bookmaker];
        const name = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;
        if (logo) {
            return <img src={logo} alt={name} className={`${size} rounded object-cover`} title={name} />;
        }
        return <span className="text-[10px] text-slate-400 text-center leading-tight" title={name}>{name}</span>;
    };

    const formatOdds = (odds) => {
        if (odds === null || odds === undefined) return '-';
        return Number(odds).toFixed(2);
    };

    const arbOverNum = parseFloat(arbOverStake);
    const arbUnderNum = parseFloat(arbUnderStake);
    const arbCalc = isArbs && !isNaN(arbOverNum) && arbOverNum > 0 && !isNaN(arbUnderNum) && arbUnderNum > 0 && bet.overOdds && bet.underOdds
        ? (() => {
            const total = arbOverNum + arbUnderNum;
            const overReturn = arbOverNum * bet.overOdds;
            const underReturn = arbUnderNum * bet.underOdds;
            const worstReturn = Math.min(overReturn, underReturn);
            const profit = worstReturn - total;
            const effectiveArbPct = total > 0 ? (profit / total) * 100 : 0;
            return { overStake: arbOverNum, underStake: arbUnderNum, total, returnAmt: worstReturn, profit, effectiveArbPct };
        })()
        : null;

    const displayArbPct = isArbs && arbCalc ? arbCalc.effectiveArbPct : edgeValue;

    const shortenMatchForPayload = (match) => {
        if (!match) return '';
        if (sport === 'afl') return match;
        const parts = match.split(' vs ');
        if (parts.length !== 2) return match;
        const last = (t) => { const w = t.trim().split(' '); return w[w.length - 1]; };
        return `${last(parts[0])} vs ${last(parts[1])}`;
    };

    const handleSubmit = async () => {
        if (!onAddToTracker) return;
        const d = bet.startTime ? new Date(bet.startTime) : new Date();
        const betDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const game = shortenMatchForPayload(bet.match);
        const sportVal = (sport || 'nba').toUpperCase();

        if (isArbs && arbCalc) {
            const overBookie = BOOKMAKER_DISPLAY_NAMES[bet.overBookmaker] || bet.overBookmaker;
            const underBookie = BOOKMAKER_DISPLAY_NAMES[bet.underBookmaker] || bet.underBookmaker;
            const overDesc = formatBetDescription(bet, 'arbs', 'over');
            const underDesc = formatBetDescription(bet, 'arbs', 'under');
            const overPayout = arbCalc.overStake * bet.overOdds;
            const underPayout = arbCalc.underStake * bet.underOdds;

            const bets = [
                {
                    date: betDate,
                    sport: sportVal,
                    game,
                    bet: overDesc,
                    odds: bet.overOdds,
                    stake: arbCalc.overStake,
                    payout: parseFloat(overPayout.toFixed(2)),
                    bookie: overBookie,
                    result: 'pending',
                    pl: 0,
                    bonus_bet: false
                },
                {
                    date: betDate,
                    sport: sportVal,
                    game,
                    bet: underDesc,
                    odds: bet.underOdds,
                    stake: arbCalc.underStake,
                    payout: parseFloat(underPayout.toFixed(2)),
                    bookie: underBookie,
                    result: 'pending',
                    pl: 0,
                    bonus_bet: false
                }
            ];

            for (const b of bets) {
                const result = await onAddToTracker(b);
                if (!result?.success) return;
            }
        } else {
            const stakeVal = parseFloat(stake);
            if (!stakeVal || stakeVal <= 0) return;

            const betDesc = formatBetDescription(bet, strategy);
            let odds, bookie;
            if (isMarketValue) {
                const opp = bet.valueOpportunities?.[0];
                odds = opp?.odds || bestOfferedPrice;
                bookie = BOOKMAKER_DISPLAY_NAMES[opp?.bookmaker || bestBookmaker] || opp?.bookmaker || bestBookmaker;
            } else {
                odds = bestOfferedPrice;
                bookie = BOOKMAKER_DISPLAY_NAMES[bestBookmaker] || bestBookmaker;
            }

            await onAddToTracker({
                date: betDate,
                sport: sportVal,
                game,
                bet: betDesc,
                odds: odds,
                stake: stakeVal,
                payout: parseFloat((stakeVal * odds).toFixed(2)),
                bookie: bookie,
                result: 'pending',
                pl: 0,
                bonus_bet: false
            });
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 pb-4 border-b border-slate-700/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                            <h3 className="text-lg font-bold text-white truncate">{bet.player}</h3>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-sm font-mono font-semibold text-slate-300 max-w-[180px] truncate" title={headerRight}>{headerRight}</span>
                            <button
                                onClick={onClose}
                                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all ml-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{shortenMatch(bet.match)}</div>
                </div>

                {/* Edge/EV + Best Book row */}
                <div className="px-5 pt-4 pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                <span className={`text-lg font-bold ${isArbs && arbCalc && arbCalc.effectiveArbPct < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                    {isArbs ? `${displayArbPct?.toFixed(2)}%` : `+${edgeValue?.toFixed(1)}%`}
                                </span>
                                <span className="text-xs text-slate-500 font-medium">{edgeLabel}</span>
                            </div>
                            {strategy === 'market_and_implied_ev' && opp?.['2025_implied_ev'] != null && opp?.['2025_implied_odds'] != null && opp['2025_implied_odds'] !== 0 && opp['2025_implied_odds'] !== 100 && (
                                <div className="flex items-center gap-2 pl-3 border-l border-slate-700/50">
                                    <span className={`text-lg font-bold ${opp['2025_implied_ev'] >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {opp['2025_implied_ev'] >= 0 ? '+' : ''}{opp['2025_implied_ev'].toFixed(1)}%
                                    </span>
                                    <span className="text-xs text-slate-500 font-medium">Implied Edge %</span>
                                </div>
                            )}
                        </div>
                        {!isArbs && (
                            <div className="flex items-center gap-2">
                                {renderBookmakerLogo(bestBookmaker, 'w-6 h-6')}
                                <span className="text-lg font-bold text-white font-mono">{formatOdds(bestOfferedPrice)}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Arbs: Over/Under sides */}
                {isArbs && (
                    <div className="px-5 pb-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/30 text-center flex flex-col">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Over</div>
                                <div className="flex flex-col items-center gap-1.5 flex-1">
                                    {renderBookmakerLogo(bet.overBookmaker, 'w-7 h-7')}
                                    <span className="text-lg font-bold text-white font-mono">{formatOdds(bet.overOdds)}</span>
                                    <div className="h-0.5 w-5 rounded-full bg-emerald-400"></div>
                                    {bet.overSource === 'prop' && bet.overSelection && (
                                        <div className="text-[10px] text-slate-500 truncate max-w-[140px]" title={bet.overSelection}>
                                            {bet.overSelection}
                                        </div>
                                    )}
                                </div>
                                {arbCalc && (
                                    <div className="text-[10px] text-slate-400 font-mono mt-2">
                                        Payout: <span className="text-white">${(arbCalc.overStake * bet.overOdds).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/30 text-center flex flex-col">
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Under</div>
                                <div className="flex flex-col items-center gap-1.5 flex-1">
                                    {renderBookmakerLogo(bet.underBookmaker, 'w-7 h-7')}
                                    <span className="text-lg font-bold text-white font-mono">{formatOdds(bet.underOdds)}</span>
                                    <div className="h-0.5 w-5 rounded-full bg-emerald-400"></div>
                                </div>
                                {arbCalc && (
                                    <div className="text-[10px] text-slate-400 font-mono mt-2">
                                        Payout: <span className="text-white">${(arbCalc.underStake * bet.underOdds).toFixed(2)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Bookmaker odds row (EV / Market Value only) */}
                {!isArbs && bookmakerOddsEntries.length > 0 && (
                    <div className="px-5 pb-3">
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700/30 p-3">
                            <div className="flex items-center justify-around gap-2">
                                {bookmakerOddsEntries.map((entry) => {
                                    const bk = entry.bookmaker;
                                    const isBest = bk === bestBookmaker;
                                    const price = isMarketValue
                                        ? entry.odds
                                        : (bet.selection === 'OVER' ? entry.over_price : entry.under_price);
                                    return (
                                        <div key={bk} className="flex flex-col items-center gap-1.5">
                                            {renderBookmakerLogo(bk, 'w-6 h-6')}
                                            <span className={`text-sm font-mono font-semibold ${
                                                isBest ? 'text-emerald-400' : 'text-slate-400'
                                            }`}>
                                                {formatOdds(price)}
                                            </span>
                                            <div className={`h-0.5 w-5 rounded-full ${isBest ? 'bg-emerald-400' : 'bg-transparent'}`}></div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Arb stake inputs */}
                {isArbs && (
                    <div className="px-5 pb-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Stakes</span>
                            {initArbStakes && (arbOverStake !== initArbStakes.over || arbUnderStake !== initArbStakes.under) && (
                                <button
                                    onClick={() => { setArbOverStake(initArbStakes.over); setArbUnderStake(initArbStakes.under); }}
                                    className="text-[10px] text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
                                >
                                    Reset to optimal
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Over Stake</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                    <input
                                        ref={stakeInputRef}
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={arbOverStake}
                                        onChange={(e) => setArbOverStake(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full pl-7 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Under Stake</label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={arbUnderStake}
                                        onChange={(e) => setArbUnderStake(e.target.value)}
                                        placeholder="0.00"
                                        className="w-full pl-7 pr-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                                    />
                                </div>
                            </div>
                        </div>
                        {arbCalc && (
                            <div className="flex gap-3 mt-3">
                                <div className="flex-1 p-2.5 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-sm font-bold text-white font-mono">${arbCalc.total.toFixed(2)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Total Stake</div>
                                </div>
                                <div className="flex-1 p-2.5 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-sm font-bold text-white font-mono">${arbCalc.returnAmt.toFixed(2)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Guaranteed Payout</div>
                                </div>
                                <div className="flex-1 p-2.5 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-center">
                                    <div className={`text-sm font-bold font-mono ${arbCalc.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>${arbCalc.profit.toFixed(2)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Profit</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Stats row */}
                <div className={`px-5 pb-4 ${isArbs ? 'hidden' : ''}`}>
                    <div className="flex gap-3">
                        {isMarketValue ? (
                            <>
                                <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-lg font-bold text-white font-mono">{formatOdds(opp?.avgMarketOdds)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{strategy === 'sb_baseline' ? 'SB Price' : 'Avg Price'}</div>
                                </div>
                                {strategy === 'market_and_implied_ev' && opp?.['2025_implied_odds'] != null && opp['2025_implied_odds'] !== 0 && opp['2025_implied_odds'] !== 100 ? (
                                    <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                        <div className="text-lg font-bold text-white font-mono">{formatOdds(opp['2025_implied_odds'])}</div>
                                        {opp['2025_hit_rate_percentage'] != null && (
                                            <div className="text-[10px] text-slate-400 mt-0.5">
                                                {opp['2025_line_hit']}/{opp['2025_total_games']} ({opp['2025_hit_rate_percentage']}%)
                                            </div>
                                        )}
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Implied Odds (2025)</div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                            <div className="text-lg font-bold text-white">{opp?.impliedProbability || '-'}</div>
                                            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Implied</div>
                                        </div>
                                        <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                            <div className="text-lg font-bold text-white">{opp?.avgMarketImpliedProbability || '-'}</div>
                                            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{strategy === 'sb_baseline' ? 'SB Implied' : 'Avg Implied'}</div>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-lg font-bold text-white font-mono">{formatOdds(computedFairOdds != null ? computedFairOdds : bet.avgFairOdds)}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Fair Odds</div>
                                </div>
                                <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-lg font-bold text-white">{bet.impliedProbability}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Implied</div>
                                </div>
                                <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30 text-center">
                                    <div className="text-lg font-bold text-white">{bet.fairImpliedProbability || '-'}</div>
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">Fair Implied</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Stake input + payout preview (non-arb only) */}
                {!isArbs && (
                    <div className="px-5 pb-5 border-t border-slate-700/50 pt-4">
                        <label className="block text-sm text-slate-300 mb-2 font-medium">Stake</label>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                                <input
                                    ref={stakeInputRef}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={stake}
                                    onChange={(e) => setStake(e.target.value)}
                                    placeholder="0.00"
                                    className="w-full pl-7 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors"
                                />
                            </div>
                            <div className="flex gap-1.5 flex-1">
                                {[{ label: '1U', mult: 1 }, { label: '0.5U', mult: 0.5 }, { label: '0.2U', mult: 0.2 }].map((unit) => {
                                    const unitVal = (unitSize * unit.mult).toFixed(2);
                                    return (
                                        <button
                                            key={unit.label}
                                            onClick={() => setStake(unitVal)}
                                            className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all border ${
                                                stake === unitVal
                                                    ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30'
                                                    : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white hover:border-slate-600'
                                            }`}
                                        >
                                            {unit.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {hasPayout && (
                            <div className="flex items-center justify-between mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                                <span className="text-sm text-slate-300">Potential Payout</span>
                                <span className="text-emerald-400 font-semibold font-mono">${payout}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Arb warning */}
                {isArbs && (
                    <div className="px-5 pb-3">
                        <div className="flex items-center gap-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                            <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="text-[11px] text-amber-300/90 leading-relaxed">Please make sure advertised odds are still available on both books before placing the bet!</span>
                        </div>
                    </div>
                )}

                {/* Action buttons */}
                <div className="px-5 pb-5">
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-slate-400 hover:text-white text-sm font-medium transition-colors rounded-xl hover:bg-slate-800/50"
                        >
                            Cancel
                        </button>
                        <button
                            disabled={isAddingBet || (isArbs ? !arbCalc : !hasPayout)}
                            onClick={handleSubmit}
                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                        >
                            {isAddingBet ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Adding...
                                </span>
                            ) : 'Add to Tracker'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default AddToTrackerModal;
